// @ts-nocheck
import { Card, Code, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import { ScrollReveal, Parallax, StaggeredList } from '../../../components/animations';

export default function ScrollPage() {
  const listItems = [
    'First item - appears with delay',
    'Second item - appears with more delay',
    'Third item - appears with even more delay',
    'Fourth item - staggered animation',
    'Fifth item - smooth sequential reveal',
  ];

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Scroll Animations</Title>
        <Text c="dimmed" size="sm">
          Animations triggered by scrolling - reveal elements as they enter the viewport
        </Text>
      </Stack>

      {/* ScrollReveal */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>ScrollReveal</Title>
            <Text size="sm" c="dimmed">
              Reveal elements as they scroll into view
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Text size="xs" c="dimmed" mb="md">
              Scroll down to see cards reveal
            </Text>

            <Stack gap="md">
              <ScrollReveal>
                <Card variant="subtle" p="md">
                  <Text fw={600}>Card 1</Text>
                  <Text size="sm" c="dimmed">
                    This card reveals when scrolled into view
                  </Text>
                </Card>
              </ScrollReveal>

              <ScrollReveal delay={200}>
                <Card variant="subtle" p="md">
                  <Text fw={600}>Card 2</Text>
                  <Text size="sm" c="dimmed">
                    This card has a 200ms delay
                  </Text>
                </Card>
              </ScrollReveal>

              <ScrollReveal delay={400}>
                <Card variant="subtle" p="md">
                  <Text fw={600}>Card 3</Text>
                  <Text size="sm" c="dimmed">
                    This card has a 400ms delay
                  </Text>
                </Card>
              </ScrollReveal>
            </Stack>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<ScrollReveal>
  <Card variant="subtle">
    <Text fw={600}>Card 1</Text>
    <Text size="sm" c="dimmed">
      This card reveals when scrolled into view
    </Text>
  </Card>
</ScrollReveal>

<ScrollReveal delay={200}>
  <Card variant="subtle">
    <Text fw={600}>Card 2</Text>
    <Text size="sm" c="dimmed">
      This card has a 200ms delay
    </Text>
  </Card>
</ScrollReveal>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • animation - 'fade' | 'slide-up' | 'slide-down' | 'scale' (default: 'fade')
              <br />
              • duration - Animation duration in ms (default: 600)
              <br />
              • delay - Delay after entering viewport (default: 0)
              <br />
              • threshold - Visibility threshold 0-1 (default: 0.1)
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Parallax */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Parallax</Title>
            <Text size="sm" c="dimmed">
              Create depth with parallax scrolling effect
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <div style={{ height: 300, overflow: 'hidden', position: 'relative' }}>
              <Parallax speed={0.5}>
                <Card
                  variant="gradient"
                  gradientVariant="primary"
                  p="xl"
                  style={{ height: 200 }}
                >
                  <Stack gap="xs" justify="center" h="100%">
                    <Text c="white" fw={700} size="xl">
                      Parallax Background
                    </Text>
                    <Text c="white" opacity={0.9}>
                      Scrolls at 50% speed
                    </Text>
                  </Stack>
                </Card>
              </Parallax>
            </div>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Parallax speed={0.5}>
  <Card variant="gradient" gradientVariant="primary">
    <Text c="white" fw={700} size="xl">
      Parallax Background
    </Text>
    <Text c="white" opacity={0.9}>
      Scrolls at 50% speed
    </Text>
  </Card>
</Parallax>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • speed - Parallax speed multiplier (default: 0.5)
              <br />
              • direction - 'vertical' | 'horizontal' (default: 'vertical')
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* StaggeredList */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>StaggeredList</Title>
            <Text size="sm" c="dimmed">
              Animate list items with sequential delays
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <StaggeredList stagger={100}>
              {listItems.map((item, index) => (
                <Card key={index} variant="outline" p="sm">
                  <Text size="sm">{item}</Text>
                </Card>
              ))}
            </StaggeredList>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const items = [
  'First item',
  'Second item',
  'Third item',
  'Fourth item',
  'Fifth item',
];

<StaggeredList stagger={100}>
  {items.map((item, index) => (
    <Card key={index} variant="outline">
      <Text size="sm">{item}</Text>
    </Card>
  ))}
</StaggeredList>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • stagger - Delay between items in ms (default: 100)
              <br />
              • animation - 'fade' | 'slide' | 'scale' (default: 'fade')
              <br />
              • duration - Animation duration in ms (default: 300)
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Landing Pages</Text>
              <Text size="xs" c="dimmed">
                Use ScrollReveal for hero sections, feature blocks, and testimonials to create engaging scroll experiences.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Long-Form Content</Text>
              <Text size="xs" c="dimmed">
                Apply Parallax to background images and sections for depth and visual interest in articles or portfolios.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Product Galleries</Text>
              <Text size="xs" c="dimmed">
                Use StaggeredList for product grids, pricing tables, or feature comparisons with sequential reveals.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Data Dashboards</Text>
              <Text size="xs" c="dimmed">
                Apply ScrollReveal to charts, cards, and stats as users scroll through dashboard sections.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
